import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
//generics
import { useNavigate } from "react-router-dom";
import { Tooltip } from "react-tooltip";
//components
import {
  pedidosAction,
  protocolosAction,
} from "components/ApplicationInsights/eventNames";
import { useCustomEvent } from "components/ApplicationInsights";
import RefreshButton from "components/RefreshButton";
import { Visibility } from "components/Visibility";
import { TableDropdown } from "./TableDropdown";
import { Row, Column } from "../Layout/Grid";
import { TableItem } from "./TableItem";
import Paginator from "../Paginator";
import { Button } from "../Button";
import Loading from "../Loading";
import Text from "../Text";
//assets
import iconOrderInactive from "img/order-inactive-icon.svg";
import iconOrderActiveDesc from "img/order-desc-icon.svg";
import iconOrderActiveAsc from "img/order-asc-icon.svg";
//interfaces
import {
  orderStatus,
  statusLockedButton,
  statusProcessing,
  statusSent,
  supplierStatusLockedButton,
  supplierStatusSent,
  TablePortalProps,
} from "./interface";
import { ITableModel } from "../Table/interface";
//styles
import { colors } from "pages/Quotes/styles/colors";
import "./styles.scss";

/**
 * @param {Array<ITableModel>} model
 * @param {Object} modelItem {
 * @param {string} label It's a table label that renders in a column.
 * @param {string} [key] If it's provided, a Text component is rendered with this key value.
 * @param {Function} [Component] Render a custom component instead Text and it recives an object of row to use that in render
 *
 * Ex: Component: (data) => <Button>{data.any}</Button>
 * @param {number} [size] It's a minimum size of row item
 * @param {number} [maxSize] It's a maximum size of row item (if you don't pass this property, the row item turns flex)
 * @modelItem}
 *
 * @param {boolean} [completeBlankSpaces] If it's true, render more table rows to match "pageSize" number
 */
export const TablePortal: React.FC<TablePortalProps> = (props) => {
  const [expandedDropdown, setExpandedDropdown] = useState<number | undefined>(
    undefined
  );
  const {
    data,
    model,
    error,
    pageSize,
    onReload,
    className,
    rowHeight,
    isRanking,
    totalPages,
    tableOrder,
    currentPage,
    customProps,
    trackingKey,
    redirectUrl,
    renderButton,
    bottomButton,
    onPageChange,
    setTableOrder,
    showPagination,
    DropdownTemplate,
    orderRadioSelected,
    completeBlankSpaces,
  } = props;

  const isEven = (el: number) => (el % 2 !== 0 ? "bg-white" : "bg-gray-10");

  const trackOrderBy = useCustomEvent(trackingKey ?? "", "Ordenar");

  const [hideShowTooltip, setHideShowTooltip] = useState(false);

  const orderToOData = (str?: string) =>
    str?.split(" ")[0].replace(".", "/") ?? str;
  const isOrderAsc = (str: string) => str.split(" ")[1] === "asc";
  const isOrderDesc = (str: string) => str.split(" ")[1] === "desc";
  const orderToAsc = (str?: string) => `${str} asc` || str;
  const orderToDesc = (str?: string) => `${str} desc` || str;

  const wrapperRef = useRef(null);
  useMouseMove(wrapperRef);

  const componentName = "TablePortal";
  const trackMenuClick = useCustomEvent(componentName, "DashboardPortal");
  const navigate = useNavigate();

  useEffect(() => {
    setExpandedDropdown(undefined);
  }, [data]);

  function useMouseMove(ref: any) {
    useEffect(() => {
      let hideShowTooltipInside = false;
      function handleMoveMouse(event: any) {
        if (ref.current && !ref.current.contains(event.target)) {
          setHideShowTooltip(true);
          hideShowTooltipInside = true;
        }
      }

      !hideShowTooltipInside &&
        document.addEventListener("mouseover", handleMoveMouse);
      return () => {
        !hideShowTooltipInside &&
          document.removeEventListener("mouseover", handleMoveMouse);
      };
    }, [ref]);
  }

  const colorRowTable = useCallback(
    (index: number) => {
      return isEven(index);
    },
    [data]
  );

  const handleClickTableLabel = (field: ITableModel) => {
    if (!field.noFilter) {
      if (props.trackingKey)
        trackOrderBy({ item: trackingKey, chave: field.key });
      setTableOrder((order: any) => sortOrderTable(field, order));
    }
  };

  const sortOrderTable = (field: ITableModel, sort: string) => {
    const isFieldKey = typeof field.key !== "undefined";

    if (isFieldKey && orderToOData(sort) !== orderToOData(field.key))
      return orderToAsc(orderToOData(field.key));

    if (
      isFieldKey &&
      orderToOData(sort) === orderToOData(field.key) &&
      isOrderAsc(sort)
    )
      return orderToDesc(orderToOData(field.key));

    return isFieldKey &&
      orderToOData(sort) === orderToOData(field.key) &&
      isOrderDesc(sort)
      ? orderToAsc(orderToOData(field.key))
      : "";
  };

  const iconSortOrderTable = (field: ITableModel) => {
    const isOrderToOData = orderToOData(tableOrder) === orderToOData(field.key);

    if (isOrderToOData && isOrderAsc(tableOrder)) return iconOrderActiveAsc;

    return isOrderToOData && isOrderDesc(tableOrder)
      ? iconOrderActiveDesc
      : iconOrderInactive;
  };

  /**
   * Render table label row
   */
  const TableLabel = useMemo(
    () =>
      model.map((m, _) => (
        <Row
          key={m.key}
          className={`align-center ${!isRanking && "bg-primary"} ${m.isButton ? "px-1" : "py-2 px-2"
            } ${m.isCenter ? "flex-1 justify-center" : "flex-1"} ${m.alignRight ? "align-right" : ""
            }`}
          style={{
            minHeight: rowHeight ?? 32,
            height: rowHeight ?? 32,
            maxHeight: rowHeight ?? 32,
            minWidth: m.size ?? 100,
            maxWidth: m.maxSize ?? "auto",
            justifyContent: m.alignRight ? "flex-end" : "",
          }}
        >
          {m.label && (
            <Button
              tooltip={m.tooltipText}
              toolTipPlace="left"
              className={`d-flex align-center ${m.isButton ? "px-1" : "p-0"
                } flex-wrap`}
            >
              <Row>
                <Text
                  size={12}
                  className="text-left color-white font-bold text-wrap"
                >
                  {m.label}
                </Text>
                <Visibility visible={!m.noFilter}>
                  <img
                    className="ml-1"
                    src={iconSortOrderTable(m)}
                    onClick={() => handleClickTableLabel(m)}
                    alt=""
                  />
                </Visibility>
              </Row>
              {m.iconFilter && m.iconFilter}
            </Button>
          )}
        </Row>
      )),
    [data, model]
  );

  const TableRow = (propsRow: any) =>
    useMemo(
      () => (
        <Row
          className={`align-center ${propsRow.modelItem.isButton ? "px-1" : "py-2 px-2"
            } h-100 ${!isRanking && colorRowTable(propsRow.objectIndex)}
            ${!propsRow.typeCursor && "no-click-cursor"}
            ${propsRow.modelItem.maxSize ? "cell-capped" : ""}`}
          style={{
            minWidth: propsRow.modelItem.size || 100,
            maxWidth: propsRow.modelItem.maxSize || "auto",
            overflow: propsRow.modelItem.maxSize ? "hidden" : "visible",
            justifyContent: propsRow.modelItem.isCenter ? "center" : "",
            height: rowHeight ?? 50,
            flex: orderRadioSelected ? "5 0" : "1",
          }}
        >
          {isRanking && propsRow.modelItem.key === "item" && (
            <Text className="number-order-ranking">
              {propsRow.objectIndex + 1}
            </Text>
          )}
          {propsRow.children}
        </Row>
      ),
      [propsRow]
    );

  const TableRowExtra = () => {
    const extraCount = pageSize ?? 20;
    const extraTableRender: any = [];
    if (data && data.length < extraCount && completeBlankSpaces) {
      for (let i = data.length; i < extraCount; i++) {
        extraTableRender.push(
          <Row
            key={i}
            className={`align-center min-w-100 ${isRanking && "item-ranking-table"
              }`}
            style={{ height: 50 }}
          >
            {model.map((modelRow, _) => (
              <TableRow
                key={_}
                modelItem={modelRow}
                keyIndex={i}
                objectIndex={i}
              />
            ))}
          </Row>
        );
      }
    }

    return extraTableRender;
  };

  const arrowWithLinkDashboard = (memoRowWithLinkDashboard: any) => {
    const conditionalStatusLocked =
      statusLockedButton.includes(memoRowWithLinkDashboard.item.status) &&
      supplierStatusLockedButton.includes(
        memoRowWithLinkDashboard.item.supplierStatus
      );
    const conditionalStatusSent =
      statusSent.includes(memoRowWithLinkDashboard.item.status) &&
      supplierStatusSent.includes(memoRowWithLinkDashboard.item.supplierStatus);
    const processingStatus = statusProcessing.includes(
      memoRowWithLinkDashboard.item.status
    );
    const orderStatusConditional = orderStatus.includes(
      memoRowWithLinkDashboard.item.status
    );

    return (
      <Row
        className={`align-center w-100 ${isRanking && "item-ranking-table"
          } custom-indicators`}
        style={{ height: rowHeight ?? 50 }}
      >
        {model.map((m, _) =>
          (m.key === "blockedQuote" && conditionalStatusLocked) ||
            m.key !== "blockedQuote" ? (
            <TableRow
              key={_}
              modelItem={m}
              keyIndex={_}
              typeCursor={
                !processingStatus &&
                !orderStatusConditional &&
                (conditionalStatusLocked || conditionalStatusSent)
              }
              objectIndex={memoRowWithLinkDashboard.i}
            >
              <TableItem
                trackingKeyProps={memoRowWithLinkDashboard.trackingKey}
                item={memoRowWithLinkDashboard.item}
                modelItem={m}
                customProps={customProps}
              />
            </TableRow>
          ) : (
            <TableDropdown
              key={_}
              data={data}
              isRanking={isRanking}
              orderStatus={orderStatusConditional}
              renderButton={renderButton}
              colorRowTable={colorRowTable}
              index={memoRowWithLinkDashboard.i}
              expandedDropdown={expandedDropdown}
              DropdownTemplate={DropdownTemplate}
              setExpandedDropdown={setExpandedDropdown}
              conditionalStatusSent={conditionalStatusSent}
            />
          )
        )}
      </Row>
    );
  };

  const renderDropdownTemplate = (propsMemoRow: any) => {
    const dropdownCheck =
      expandedDropdown === propsMemoRow.i && DropdownTemplate;
    const confirmedOrderStatus = propsMemoRow.item.status === "CONFIRMED";
    const canceledOrderStatus = propsMemoRow.item.status === "CANCELED";

    if (dropdownCheck && !confirmedOrderStatus) {
      return (
        DropdownTemplate?.Quote && (
          <DropdownTemplate.Quote
            {...{ ...propsMemoRow.item, ...customProps }}
          />
        )
      );
    }

    if (dropdownCheck && (confirmedOrderStatus || canceledOrderStatus)) {
      return (
        DropdownTemplate?.Order && (
          <DropdownTemplate.Order
            {...{ ...propsMemoRow.item, ...customProps }}
          />
        )
      );
    }
  };

  const MemoRow = (propsMemoRow: any) =>
    useMemo(() => {
      return (
        <Column
          className="relative"
          style={Object.assign(
            redirectUrl != null ? { display: "flex" } : { display: "grid" }
          )}
          key={propsMemoRow.i}
        >
          {arrowWithLinkDashboard(propsMemoRow)}
          {renderDropdownTemplate(propsMemoRow)}
        </Column>
      );
    }, [propsMemoRow]);

  const trackingAction = async (button: any) => {
    trackMenuClick({
      item:
        button.linkTo === "/protocolos"
          ? protocolosAction.LINK_DASHBOARD_VERTODOSPROTOCOLOS
          : pedidosAction.LINK_DASHBOARD_VERTODOSPEDIDOS,
    });

    navigate(button.linkTo);
  };

  const valueMinHeightBottomBtn = bottomButton ? 52.8 : 0;

  const handleNoDataText = () => {
    if (orderRadioSelected)
      return "Nenhum pedido encontrado para o filtro selecionado";

    return "Nenhuma cotação encontrada para o filtro selecionado";
  };

  return (
    <>
      <Column
        className={`relative flex-1 overflow-x-hidden ${className}` || ""}
        style={{
          minHeight:
            typeof data === "undefined"
              ? 50 * (pageSize ?? 10) + 50 + valueMinHeightBottomBtn
              : "auto",
          borderRadius: "10px 10px 0 0",
        }}
      >
        <Visibility visible={typeof data === "undefined"}>
          <>
            <Visibility visible={!error}>
              <Loading size={45} />
            </Visibility>

            <Visibility visible={!!error}>
              <RefreshButton onReload={onReload} />
            </Visibility>
          </>
        </Visibility>

        <Column className="relative flex-1 overflow-x-auto">
          <Row
            className={`align-center ${isRanking && "label-title-ranking"}`}
            style={{
              minHeight: rowHeight ?? 32,
              height: rowHeight ?? 32,
              maxHeight: rowHeight ?? 32,
            }}
          >
            {TableLabel}
          </Row>
          {data &&
            data?.map(
              (item, i) =>
                i < (pageSize ?? i + 1) && (
                  <MemoRow
                    key={i}
                    trackingKeyProps={props.trackingKey}
                    item={item}
                    i={i}
                  />
                )
            )}
          <Visibility visible={(data && data.length === 0) ?? false}>
            <Text
              size={14}
              className="text-center py-3 font-bold w-sm-100"
              color={colors.greenDarkViveo}
            >
              {handleNoDataText()}
            </Text>
          </Visibility>
          <TableRowExtra />
          {hideShowTooltip && <Tooltip variant="light" />}
        </Column>

        {bottomButton && typeof data !== "undefined" && (
          <Button
            onClick={() => trackingAction(bottomButton)}
            className="mt-2 w-100 px-0"
          >
            <Row
              className="bg-gray-20 py-2 d-flex justify-center align-items w-100"
              style={{
                borderRadius: "0 0 10px 10px",
                height: 40,
                maxHeight: 40,
              }}
            >
              <Text
                size={12}
                className="d-flex align-center justify-center color-primary font-bold"
              >
                {bottomButton.label}
              </Text>
            </Row>
          </Button>
        )}
      </Column>

      <Row
        className={`align-center ajust-footer-elements customer justify-center`}
      >
        <Column>
          {pageSize &&
            !bottomButton &&
            typeof data !== "undefined" &&
            totalPages &&
            (showPagination == null || showPagination === true) &&
            totalPages > 1 ? (
            <Paginator
              pageSize={pageSize}
              currentPage={currentPage}
              totalPage={totalPages}
              onPageChange={onPageChange}
            />
          ) : null}
        </Column>
      </Row>
    </>
  );
};
